import {
  BitkitBadgeProps,
  BitkitIconComponent,
  BitkitTabsTriggerProps,
  IconArrowDown,
  IconArrowUp,
  IconDot,
} from '@bitrise/bitkit-v2';

import type { StreamType } from '@/pages/Insights/pages/InsightsExploreBuildCache/InsightsExploreBuildCache.types';
import type { ExploreBuildsEntity } from '@/pages/Insights/pages/InsightsExploreBuilds/InsightsExploreBuilds.types';
import type { ExploreTestsEntity } from '@/pages/Insights/pages/InsightsExploreTests/InsightsExploreTests.types';
import type {
  ExploreCreditsConcurrencies,
  ExploreCreditsEntity,
  ExploreCreditsQueuesEntity,
  ExploreInfraVCPUConcurrencies,
  ExploreStackedTotalTimeEntity,
} from '@/pages/Insights/pages/InsightsExploreUtilization/InsightsExploreUtilization.types';
import { LineColor } from '@/pages/Insights/utils/getLineColor';

import type { ExploreGitEntity } from './pages/InsightsExploreGit/InsightsExploreGit.types';
import type { ExplorePipelinesEntity } from './pages/InsightsExplorePipelines/InsightsExplorePipelines.types';

export type TypeDuration =
  | 'FIXED'
  | 'P1D'
  | 'P2D'
  | 'P3D'
  | 'P5D'
  | 'P7D'
  | 'P14D'
  | 'P30D'
  | 'P45D'
  | 'P60D'
  | 'P90D'
  | 'P12W'
  | 'P24W'
  | 'P52W'
  | 'P72W'
  | 'P3M'
  | 'P6M'
  | 'P12M'
  | 'P18M'
  | 'P24M';

export type TypeTimeInterval = 'secondly' | 'hourly' | 'daily' | 'weekly' | 'monthly';

export type TypeMetricUnit =
  | 'none'
  | 'percent'
  | 'seconds'
  | 'thousandSeparatedCount'
  | 'bytes'
  | 'bytesToMB'
  | 'secondsToHours'
  | 'secondsToMinutes'
  | 'gigabytePerSecond'
  | 'gigabitPerSecond'
  | 'gibibyte';

export type DataType = 'builds' | 'credits' | 'tests' | 'build_cache' | 'commands' | 'git' | 'pipelines';

export type StatsType = {
  trend: number;
  value: number;
};

export type DataPoint = {
  timestamp: number;
  value: number;
};

export type GeneralBreakdownType = {
  data: DataPoint[];
  title: string;
};

export type ProjectType = {
  id: string;
  title: string;
  data: DataPoint[];
};

export type AppType = GeneralBreakdownType;

export type PipelineType = GeneralBreakdownType;

export type StageType = PipelineType;

export type WorkflowType = PipelineType;

export type TestSuiteType = PipelineType;

export type BranchType = PipelineType;

export type BuildToolType = GeneralBreakdownType;

export type CommandType = GeneralBreakdownType;

export type HostnameType = GeneralBreakdownType;

export type UsernameType = GeneralBreakdownType;

export type CIProviderType = GeneralBreakdownType;

export type StepType = PipelineType;

export type DataUnitString = 'builds' | 'credits';

export type Trend = {
  colorVariant: BitkitBadgeProps['colorVariant'];
  icon: BitkitIconComponent;
  iconColor: BitkitTabsTriggerProps['iconColor'];
};

export type PrimaryTabProps = Pick<BitkitTabsTriggerProps, 'icon' | 'iconColor' | 'secondaryText'>;

export const trendProps: Record<'down' | 'stagnant' | 'up', Trend> = {
  down: {
    colorVariant: 'green',
    icon: IconArrowDown,
    iconColor: 'icon/positive',
  },
  stagnant: {
    colorVariant: 'neutral',
    icon: IconDot,
    iconColor: 'icon/secondary',
  },
  up: {
    colorVariant: 'red',
    icon: IconArrowUp,
    iconColor: 'icon/negative',
  },
};

export type DataColorsType = {
  [name: string]: LineColor;
};

export type PageId =
  | 'overview'
  | 'bottlenecks'
  | 'explore/builds'
  | 'explore/credits'
  | 'explore/utilization'
  | 'explore/tests'
  | 'explore/build-cache'
  | 'explore/commands'
  | 'explore/git'
  | 'explore/pipelines'
  | 'dashboard_list'
  | 'dashboard_details'
  | 'alerts_list'
  | 'alerts_details'
  | 'alerts_create_and_edit';

export type BuildsMetricType = 'time_success_p90' | 'time_success_p50' | 'failure_rate' | 'total_count' | 'total_time';
export type TestsMetricType =
  'time_success_p90' | 'time_success_p50' | 'failure_rate' | 'total_duration' | 'total_runs' | 'flaky_runs';
export type CreditsMetricType = 'total_credits';

export type UtilizationMetricType =
  | 'total_ai_credits'
  | 'code_push_active_users'
  | 'code_push_downloads'
  | 'code_push_data_transfer'
  | 'total_credits'
  | 'total_time'
  | 'network_egress'
  | 'workflow_runs'
  | 'artifact_storage'
  | 'concurrencies'
  | 'ci_vcpu_concurrencies'
  | 'infrastructure_vcpu_concurrencies'
  | 'queues'
  | 'invocation_count'
  | 'build_hub_machine_minutes'
  | 'rbe_machine_minutes'
  | 'infrastructure_cost';

export type BuildCacheMetricType =
  'invocation_count' | 'transfer_p50' | 'transfer_p90' | 'cache_hit_rate_p10' | 'cache_hit_rate_p50';

export type CommandsMetricType = 'invocation_count' | 'duration_p50' | 'duration_p90' | 'error_rate';

export type GitMetricType = 'cycle_time' | 'merge_frequency';

export type PipelinesMetricType = 'total_count';

export type ExploreMetricType =
  | BuildsMetricType
  | TestsMetricType
  | CreditsMetricType
  | BuildCacheMetricType
  | CommandsMetricType
  | GitMetricType
  | PipelinesMetricType
  | UtilizationMetricType;

export type BreakdownMetricType = StreamType;

export type ExploreData = Partial<
  Record<
    ExploreMetricType,
    | ExploreBuildsEntity
    | ExploreTestsEntity
    | ExploreCreditsEntity
    | ExploreGitEntity
    | ExplorePipelinesEntity
    | ExploreStackedTotalTimeEntity
    | ExploreCreditsConcurrencies
    | ExploreCreditsQueuesEntity
    | ExploreInfraVCPUConcurrencies
  >
>;

/** Each filter's selected values, keyed by filter. */
export type InsightsFilterState = Record<string, string[]>;

export type QueryFilters = {
  project_id: string[];
  branch: string[];
  ci_provider: string[];
  machine_type: string[];
  module: string[];
  hostname: string[];
  pipeline: string[];
  stack: string[];
  stage: string[];
  status: string[];
  step: string[];
  step_status: string[];
  test_case_name: string[];
  test_suite_name: string[];
  tool: string[];
  workflow: string[];
  username: string[];
  [key: string]: string[];
};

export type ChartDataEntity = {
  dataPoints: DataPoint[];
  limit?: number;
  name: string;
  displayName?: string;
  trendlineDataPoints?: DataPoint[];
};

// Mirrors the subset of recharts' mouse-handler state (MouseHandlerDataParam) that Chart.tsx reads.
// recharts v3 no longer exposes chartX/chartY (raw pointer coords) here — those come from the event.
export type ChartState = {
  activeLabel?: string | number;
  activeCoordinate?: { x: number; y: number };
};

export type ChartDataPoint = {
  timestamp: number;
  [key: string]: number | string;
};

export type InsightsFilterType = DataType | 'bottlenecks';
